<template>
    <div class="CompanyListPage">
        <DashboardSectionHeader title="Companies">
            <Button class="primary" @click="openAddCompanyModal">Add Company</Button>
        </DashboardSectionHeader>

        <DashboardFilterHeader type="company" @change="handleFilterChange"/>

        <DashboardList
            :columns="['Company', 'Subscription', 'Drivers', 'Tickets', 'Last Submitted']"
            :isLoading="isFetching"
            :isComplete="isComplete"
            @loadMore="fetchMore"
        >
            <DashboardListItem
                v-for="company in companies"
                :key="company.id"
                :columns="[
                    company.name,
                    subscriptionStatus(company),
                    company.activeDriverCount,
                    company.ticketCount,
                    company.lastTicketSubmittedAt ? formatDate(company.lastTicketSubmittedAt) : ''
                ]"
                :actions="[
                    { label: 'View', click: () => handleViewCompany(company) },
                    { label: 'View Details', click: () => handleViewCompany(company, '/company/admin') },
                    { label: 'Delete', variant: 'danger', click: () => handleDeleteCompany(company) },
                ]"
            />
        </DashboardList>
    </div>
</template>

<script>
import * as api from '@/api';
import DashboardFilterHeader from '@/components/DashboardFilterHeader';
import DashboardSectionHeader from '@/components/DashboardSectionHeader';
import DashboardList from '@/components/DashboardList';
import DashboardListItem from '@/components/DashboardListItem';
import ConfirmModal from '@/components/modals/ConfirmModal';
import AdminAddCompanyModal from './components/AdminAddCompanyModal';
import { formatDate } from '@/utils/formatting';

export default {
    name: 'CompanyListPage',

    components: { DashboardSectionHeader, DashboardList, DashboardListItem, DashboardFilterHeader },

    data: () => ({
        companies: [],
        page: 0,
        isFetching: false,
        isComplete: false,

        filters: null,
    }),

    mounted() {
        this.fetchMore();
    },

    methods: {
        formatDate,
        async fetchMore(shouldResetResults = false) {
            if (this.isFetching) return;

            // Restart pagination
            if (shouldResetResults) {
                this.page = 0;
            }

            const limit = 100;
            const offset = limit * this.page;

            this.isFetching = true;
            const res = await api.getCompanies({ 
                ...(this.filters ?? {}),
                limit, 
                offset 
            });
            this.isFetching = false;

            if (!res.ok) {
                this.$awn.alert('Failed to load companies');
                return;
            }

            this.page += 1;
            this.isComplete = res.body.length < limit;

            // Avoid appending to the list if we're filtering
            if (shouldResetResults) {
                this.companies = res.body;
            } else {
                this.companies = this.companies.concat(res.body);
            }
        },

        async handleViewCompany(company, redirectUrl = '/company/dashboard') {
            this.$store.dispatch('auth/setAdminCompany', company);
            this.$router.push(redirectUrl);
        },

        async handleDeleteCompany(company) {
            const title = 'Delete Confirmation';
            const message = 'Are you sure you want to PERMANENTLY delete this company?';

            const onConfirm = async () => {
                const res = await api.deleteCompany(company.id);
                if (res.error) {
                    return this.$awn.alert('Failed to delete company');
                }

                this.$awn.success('Company has been removed');
                this.companies = this.companies.filter(c => c.id !== company.id);
            };

            this.$modal.show(
                ConfirmModal,
                { title, message, type: 'delete', confirmLabel: 'Delete', onConfirm },
                { height: 'auto' },
            );
        },

        subscriptionStatus(company) {
            return company.subscription?.status ?? '';
        },

        openAddCompanyModal() {
            this.$modal.show(AdminAddCompanyModal, {}, { height: 'auto', scrollable: true });
        },

        async handleFilterChange(filters) {
            this.filters = filters;
            await this.fetchMore(true);
        },
    },
}
</script>
